import React from "react";
import { Link } from "react-router-dom";
import { LegalPage, Part, List, Mail, Phone, COMPANY } from "@/components/Legal";

// Public privacy policy (dentmemo.in/privacy). Linked from the footer, the sign-up consent box and
// Google's OAuth consent screen, so keep the URL stable. Update UPDATED whenever the text changes.

const UPDATED = "3 November 2026";
const GRIEVANCE_OFFICER = "Tejhus Joseph";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated={UPDATED} testId="privacy-policy">
        <p className="mt-6">
          DentMemo is record-keeping software for dental clinics in India. It is operated by {COMPANY.name} (LLPIN {COMPANY.llpin}), a limited liability partnership registered in India with its
          registered office at {COMPANY.address} (“DentMemo”, “we”, “us”).
          This policy explains what information DentMemo collects, why, who it is shared with, and the choices you have.
          It applies to the website dentmemo.in and the DentMemo app.
        </p>

        <Part title="Clinic data and patient data">
          <p>
            <span className="font-semibold text-slate-800">Clinic accounts.</span> For the details of the people who sign up and use DentMemo
            (clinic owners and their staff), we decide how that information is used, as described below.
          </p>
          <p>
            <span className="font-semibold text-slate-800">Patient records.</span> Patient records are entered by a clinic and belong to that
            clinic. The clinic decides what to record and is responsible for having its patients’ consent. We store and process patient records
            only on the clinic’s behalf, to provide DentMemo to that clinic. If you are a patient, please contact your clinic first about your
            records; we will help the clinic respond.
          </p>
        </Part>

        <Part title="What we collect">
          <List items={[
            ["Account details:", "your name, email address and password. Passwords are stored only as a one-way hash, never in readable form. We also record your role (owner or staff) and that you accepted the data consent notice."],
            ["Mobile number:", "clinic owners confirm a mobile number with a one-time code sent by SMS when they sign up."],
            ["Sign in with Google:", "if you choose it, we receive your name, email address and Google account ID from Google. We do not receive your Google password or anything else from your Google account."],
            ["Clinic details:", "clinic name, logo and colours, staff code, treatment list, and the visiting consultants you add, with their fees and payouts."],
            ["Patient records entered by the clinic:", "name, age, gender, phone number, Patient ID, visits (date, diagnosis or tooth, treatment, consultant, fees, lab costs, remarks), appointments, and photos or X-rays attached to visits."],
            ["Billing:", "your plan, subscription status and the payment reference numbers from Razorpay. Card, UPI and bank details are entered on Razorpay’s own payment page; DentMemo never sees or stores them."],
            ["Technical information:", "the IP address used when an SMS code is requested (kept for 3 days to prevent misuse), standard server logs, and usage statistics from Google Analytics (pages visited, sign-up and log-in events, device and browser type), which uses cookies."],
          ]} />
          <p>
            So that DentMemo keeps working without internet, the app also keeps a copy of your clinic’s records in the browser’s storage on each
            device you sign in on. That copy stays on the device until the browser’s site data is cleared, so please only sign in on devices your
            clinic controls.
          </p>
        </Part>

        <Part title="How we use it">
          <List items={[
            ["To run DentMemo:", "store and sync your clinic’s records and show them to your clinic’s staff according to their role (for example, staff in assistant mode cannot see fees)."],
            ["To keep accounts secure:", "sign-in, SMS confirmation of new owners’ mobile numbers, and password-reset emails."],
            ["To bill for subscriptions", "and handle payment questions."],
            ["To support you", "when you contact us, and to improve DentMemo using overall usage statistics."],
            ["To meet legal obligations", "where the law requires it."],
          ]} />
          <p>We do not sell personal data, and we do not use patient records for advertising or marketing.</p>
        </Part>

        <Part title="Who we share it with">
          <p>We share information only with the service providers we need to run DentMemo, and only what each one needs:</p>
          <List items={[
            ["Amazon Web Services (AWS):", "hosts DentMemo’s servers and database in Mumbai, India."],
            ["Twilio:", "sends the SMS code; it receives the mobile number being confirmed."],
            ["Google:", "Sign in with Google, and Google Analytics for usage statistics."],
            ["Razorpay:", "processes subscription payments."],
            ["Our email provider:", "delivers password-reset emails."],
          ]} />
          <p>
            Inside DentMemo, a clinic’s records are visible only to that clinic’s owner and staff, never to other clinics. We may also disclose
            information if the law requires it, for example in response to a valid order from a court or government authority.
          </p>
        </Part>

        <Part title="Where it is stored">
          <p>
            DentMemo’s servers and database are in Mumbai, India. Some service providers, such as Twilio and Google, may process the limited
            information they receive (for example a mobile number, an email address or usage statistics) outside India.
          </p>
        </Part>

        <Part title="How long we keep it">
          <p>
            We keep clinic and patient records, including daily backups, for as long as the clinic uses DentMemo. A clinic owner can download a
            full backup at any time from Settings, and can permanently delete all of the clinic’s data, including patient records, visits, photos,
            consultants and backups, using <span className="font-semibold text-slate-800">Delete all clinic data</span> in Settings. To close an
            account completely, email <Mail />. Step-by-step instructions are on our{" "}
            <Link to="/data-deletion" className="font-semibold text-slate-900 underline underline-offset-2">data deletion page</Link>. Records of SMS codes sent are deleted automatically after 3 days.
          </p>
          <p>Clinics may have their own legal duties to keep patient records for a minimum period; it is the clinic’s decision when to delete them.</p>
        </Part>

        <Part title="How we protect it">
          <p>
            All traffic to and from DentMemo is encrypted with HTTPS. Passwords are hashed, each clinic’s data is kept separate and is limited by
            role, and new clinic owners confirm a mobile number. No system is perfectly secure; if a data breach affects you, we will inform you
            and the Data Protection Board of India as the law requires.
          </p>
        </Part>

        <Part title="Your rights">
          <p>
            Under India’s Digital Personal Data Protection Act, 2023, you can ask to access, correct or erase your personal data, withdraw your
            consent, nominate someone to act for you, and have a complaint addressed. Clinic users can update, export or delete most data
            themselves in Settings; for anything else, email <Mail />. Patients should contact their clinic, and we will help the clinic respond.
            If you are not satisfied with our response, you can complain to the Data Protection Board of India.
          </p>
        </Part>

        <Part title="Children">
          <p>
            DentMemo accounts are for adults working at a clinic. Clinics may record the treatment of children; the clinic is responsible for
            getting a parent’s or guardian’s consent.
          </p>
        </Part>

        <Part title="Google user data">
          <p>
            When you use Sign in with Google, DentMemo receives only your name, email address and Google account ID. We use them only to create
            your account and sign you in, we do not share them except as described above, and we do not use them for advertising. DentMemo’s
            use of information received from Google APIs adheres to the{" "}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer"
              className="font-semibold text-slate-900 underline underline-offset-2">Google API Services User Data Policy</a>, including the
            Limited Use requirements.
          </p>
        </Part>

        <Part title="Changes to this policy">
          <p>
            If we change this policy we will update the date at the top of this page, and we will tell clinic owners in the app or by email
            before any significant change takes effect.
          </p>
        </Part>

        <Part title="Contact and grievance officer">
          <p>
            For privacy questions, data requests or complaints, contact our grievance officer, {GRIEVANCE_OFFICER}, at <Mail /> or <Phone />,
            or write to {COMPANY.name}, {COMPANY.address}.
          </p>
        </Part>
    </LegalPage>
  );
}
